import { Component, OnInit } from '@angular/core';
import { catchError, forkJoin, map, of } from 'rxjs';
import { ConfigService } from 'app/services/config/config.service';
import { RestService } from 'app/services/rest/rest.service';
import { AppService } from 'app/services/app/app.service';
import { LocationAvailableModel } from 'app/models/location-available-model';

interface TenantLocationOption extends LocationAvailableModel {
  name: string;
  code: string;
}

@Component({
  selector: 'app-tenant-as',
  templateUrl: './tenant-as.component.html',
  styleUrls: ['./tenant-as.component.scss']
})
export class TenantAsComponent implements OnInit {
  locations: TenantLocationOption[] = [];
  selectedLocationCode: string | null = null;
  loading = false;

  constructor(
    public configService: ConfigService,
    private restService: RestService,
    private appService: AppService
  ) {}

  ngOnInit(): void {
    this.refreshLocations();
  }

  refreshLocations(): void {
    const configuredLocations = this.configService.settings?.locations || [];

    if (configuredLocations.length === 0) {
      this.locations = [];
      this.selectedLocationCode = null;
      return;
    }

    this.loading = true;

    forkJoin(
      configuredLocations.map((location) =>
        this.restService.getLocation(location.code).pipe(
          map((availability: LocationAvailableModel) => ({
            locationsId: availability?.locationsId ?? location.id,
            locationCode: availability?.locationCode ?? location.code,
            online: availability?.online ?? false,
            name: location.name || location.code,
            code: location.code
          })),
          catchError(() =>
            of({
              locationsId: location.id,
              locationCode: location.code,
              online: false,
              name: location.name || location.code,
              code: location.code
            })
          )
        )
      )
    ).subscribe({
      next: (locations) => {
        this.locations = locations;
        this.syncSelectedLocation();
      },
      error: () => {
        this.locations = configuredLocations.map((location) => ({
          locationsId: location.id,
          locationCode: location.code,
          online: false,
          name: location.name || location.code,
          code: location.code
        }));
        this.syncSelectedLocation();
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  onLocationChange(locationCode: string): void {
    this.selectedLocationCode = locationCode;

    const selectedLocation = this.selectedLocation;
    if (!selectedLocation) {
      return;
    }

    this.appService.clientSelected = {
      locationsId: selectedLocation.locationsId,
      locationCode: selectedLocation.locationCode,
      online: selectedLocation.online
    };
  }

  get selectedLocation(): TenantLocationOption | null {
    if (!this.selectedLocationCode) {
      return null;
    }

    return this.locations.find((location) => location.locationCode === this.selectedLocationCode) || null;
  }

  private syncSelectedLocation(): void {
    const storedLocation = this.appService.clientSelected;

    const selectedLocation =
      this.locations.find((location) => location.locationCode === storedLocation?.locationCode)
      || this.locations[0]
      || null;

    this.selectedLocationCode = selectedLocation?.locationCode || null;

    if (selectedLocation) {
      this.appService.clientSelected = {
        locationsId: selectedLocation.locationsId,
        locationCode: selectedLocation.locationCode,
        online: selectedLocation.online
      };
    }
  }
}
